import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import Layout from "../../../component/layout/Layout";
import { getMessageAdDetail, getReport, getStoredReport, type MomentAdDetailDto, type MomentAdDto, type StoredReportDto } from "../api/momentApi";
import MomentPhonePreview from "../components/MomentPhonePreview";
import { createMomentCommerceIntro, createMomentCoupon, MOMENT_MESSAGE_TYPE_OPTIONS, type MomentButton, type MomentCarouselItem, type MomentCommerceIntro, type MomentCoupon, type MomentListItem, type MomentMessageType } from "../data/momentOptions";
import { buildMomentClickSummary, buildMomentStructuredClickSummary, ensureArray, formatMomentMetricCount, getMomentMetricLabel, MOMENT_SUMMARY_LABELS, toMomentMetricMap } from "../utils/momentReportLabels";
import { REPORT_DATE_PRESET_OPTIONS as DATE_PRESET_OPTIONS } from "../data/momentConstants";

const SUMMARY_METRIC_ORDER = [
  "cost",
  "msg_send",
  "msg_send_fail",
  "cost_per_msg_send",
  "msg_open",
  "msg_open_rate",
  "cost_per_msg_open",
  "msg_click",
  "msg_click_rate",
  "cost_per_msg_click",
] as const;

type ReportRow = {
  dimensions?: Record<string, unknown>;
  metrics?: Record<string, unknown>;
};

type ReportLocationState = {
  ad?: MomentAdDto;
};

type LiveReportData = object;
type DisplayReportData =
  | { mode: "live"; payload: LiveReportData }
  | { mode: "stored"; payload: StoredReportDto };

export default function MomentReportPage() {
  return (
    <Layout>
      <MomentReportContent />
    </Layout>
  );
}

function MomentReportContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { kakaoAdId = "" } = useParams();
  const state = (location.state as ReportLocationState | null) ?? null;

  const [reportPreset, setReportPreset] = useState<string | null>(null);
  const [reportData, setReportData] = useState<DisplayReportData | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [adDetail, setAdDetail] = useState<MomentAdDetailDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(true);

  const ad = state?.ad ?? null;
  const availablePresetOptions = useMemo(() => {
    const sendDate = parseSendDate(adDetail?.sendDate);
    if (!sendDate) {
      return DATE_PRESET_OPTIONS;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((today.getTime() - sendDate.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) {
      return DATE_PRESET_OPTIONS.filter((option) => option.value === "TODAY");
    }
    if (diffDays >= 7) {
      return DATE_PRESET_OPTIONS.filter((option) => option.value === "LAST_7DAY");
    }
    return DATE_PRESET_OPTIONS;
  }, [adDetail?.sendDate]);

  const fetchReport = async (preset: string) => {
    if (!kakaoAdId) return;
    setReportLoading(true);
    try {
      if (preset === "TODAY") {
        const data = await getReport({
          messageAdId: kakaoAdId,
          datePreset: preset,
          metricsGroups: ["MESSAGE", "MESSAGE_ADDITION", "MESSAGE_CLICK"],
        });
        setReportData({ mode: "live", payload: data as object });
      } else {
        const data = await getStoredReport(kakaoAdId, preset as "YESTERDAY" | "LAST_7DAY" | "LAST_30DAY");
        setReportData({ mode: "stored", payload: data });
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "보고서 조회 중 오류가 발생했습니다.";
      alert(msg);
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    if (!reportPreset) {
      return;
    }
    fetchReport(reportPreset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kakaoAdId, reportPreset]);

  useEffect(() => {
    const fetchDetail = async () => {
      if (!kakaoAdId) return;
      setDetailLoading(true);
      try {
        const detail = await getMessageAdDetail(kakaoAdId);
        setAdDetail(detail);
      } catch (e) {
        console.error(e);
      } finally {
        setDetailLoading(false);
      }
    };
    fetchDetail();
  }, [kakaoAdId]);

  useEffect(() => {
    if (detailLoading) {
      return;
    }
    if (!reportPreset || !availablePresetOptions.some((option) => option.value === reportPreset)) {
      setReportPreset(resolveDefaultReportPreset(adDetail?.sendDate, availablePresetOptions));
    }
  }, [adDetail?.sendDate, availablePresetOptions, detailLoading, reportPreset]);

  return (
    <div className="moment-page tw-max-w-[1200px] tw-mx-auto tw-px-6 tw-py-10">
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-mb-6">
        <div>
          <h1 className="tw-text-2xl tw-font-bold tw-text-gray-800">메시지 광고 통계</h1>
          <p className="tw-mt-2 tw-text-xl tw-font-semibold tw-text-gray-600">{ad?.adName ?? kakaoAdId}</p>
        </div>
        <button
          onClick={() => navigate("/moment/list")}
          className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50"
        >
          목록으로
        </button>
      </div>

      <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-gray-200 tw-shadow-sm tw-p-5">
        <div className="tw-flex tw-gap-2 tw-mb-4">
          {availablePresetOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setReportPreset(opt.value)}
              className={`tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-border transition-colors ${
                reportPreset === opt.value
                  ? "tw-bg-[#5D87FF] tw-text-white tw-border-[#5D87FF]"
                  : "tw-bg-white tw-text-gray-600 tw-border-gray-300 hover:tw-bg-gray-50"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {reportLoading ? (
          <div className="tw-py-16 tw-text-center tw-text-gray-400">통계를 조회하는 중입니다.</div>
        ) : (
          <ReportPanel data={reportData} adDetail={adDetail} detailLoading={detailLoading} />
        )}
      </div>
    </div>
  );
}

function ReportPanel({ data, adDetail, detailLoading }: { data: DisplayReportData | null; adDetail: MomentAdDetailDto | null; detailLoading: boolean }) {
  const row = useMemo(() => extractFirstRow(data?.mode === "live" ? data.payload : null), [data]);
  const metrics = row?.metrics ?? {};
  const [chartMetric, setChartMetric] = useState<"msgOpen" | "msgClick" | "msgOpenRate" | "msgClickRate">("msgOpen");
  const [trendViewMode, setTrendViewMode] = useState<"daily" | "cumulative">("daily");
  const isTodayEmptyLiveReport = isEmptyLiveReportData(data);

  if (!data) {
    return <div className="tw-py-16 tw-text-center tw-text-gray-400">통계 데이터가 없습니다.</div>;
  }

  const summaryEntries: Array<[string, unknown]> =
    data.mode === "live"
      ? SUMMARY_METRIC_ORDER.map((key) => [key, metrics[key]] as [string, unknown])
      : [
          ["cost", data.payload.summary.cost],
          ["msg_send", data.payload.summary.msgSend],
          ["msg_send_fail", data.payload.summary.msgSendFail],
          ["cost_per_msg_send", Number(data.payload.summary.costPerMsgSend)],
          ["msg_open", data.payload.summary.msgOpen],
          ["msg_open_rate", Number(data.payload.summary.msgOpenRate)],
          ["cost_per_msg_open", Number(data.payload.summary.costPerMsgOpen)],
          ["msg_click", data.payload.summary.msgClick],
          ["msg_click_rate", Number(data.payload.summary.msgClickRate)],
          ["cost_per_msg_click", Number(data.payload.summary.costPerMsgClick)],
        ];

  const showTrendChart =
    data.mode === "stored" &&
    data.payload.datePreset === "LAST_7DAY";
  const useCompactMessageLayout =
    data.mode === "stored" &&
    data.payload.datePreset === "LAST_7DAY";
  const isTrendAvailable =
    data.mode === "stored" ? isValidStoredRange(data.payload.startDate, data.payload.endDate) : false;

  const trendData =
    data.mode === "stored"
      ? fillTrendDates(data.payload.startDate, data.payload.endDate, data.payload.trend)
      : [];
  const dailyTable =
    data.mode === "stored" && adDetail?.messageBody
      ? buildDailyStatTableRows(
          adDetail.messageType as MomentMessageType,
          adDetail.messageBody,
          adDetail.sendDate ?? null,
          trendData,
          data.payload.dailyClickStats
        )
      : null;
  const displayedTrendData = trendViewMode === "cumulative" ? buildCumulativeTrendData(trendData) : trendData;
  const displayedDailyTable = trendViewMode === "cumulative" ? buildCumulativeDailyTable(dailyTable) : dailyTable;

  const clickEntries: Array<[string, number]> =
    data.mode === "live"
      ? Object.entries(metrics)
          .filter(([key, value]) => key.startsWith("msg_click_") && Number(value) > 0)
          .map(([key, value]) => [key, Number(value)])
      : data.payload.clickStats.map((item) => [item.key, item.count]);
  const clickSummaryItems =
    adDetail?.messageBody
      ? buildMomentStructuredClickSummary(
          adDetail.messageType as MomentMessageType,
          adDetail.messageBody,
          data.mode === "live" ? metrics : toMomentMetricMap(data.payload.clickStats),
          false
        )
      : buildMomentClickSummary(clickEntries);
  const showButton1ImageInfo = adDetail ? hasImageBoundButton1(adDetail.messageType as MomentMessageType) : false;

  return (
    <div className="tw-space-y-6">
      {isTodayEmptyLiveReport && (
        <div className="tw-rounded-xl tw-border tw-border-sky-200 tw-bg-sky-50 tw-p-6">
          <div className="tw-text-sm tw-font-semibold tw-text-sky-800">오늘 실시간 통계가 없습니다.</div>
          <p className="tw-mt-2 tw-text-sm tw-text-sky-700">
            오늘 기준으로 집계된 열람 또는 클릭 데이터가 아직 없습니다.
          </p>
        </div>
      )}
      {summaryEntries.length > 0 && (
        <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 xl:tw-grid-cols-5 tw-gap-3">
          {summaryEntries.map(([key, value]) => (
              <div key={key} className="tw-rounded-xl tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-4">
              <div className="tw-text-xs tw-font-medium tw-text-gray-500">{getMomentMetricLabel(key)}</div>
              <div className="tw-mt-2 tw-text-lg tw-font-bold tw-text-gray-800">{formatMetricValue(key, value)}</div>
            </div>
          ))}
        </div>
      )}

      {showTrendChart && isTrendAvailable && (
        <div className="tw-rounded-xl tw-border tw-border-gray-200 tw-overflow-hidden">
          <div className="tw-bg-gray-50 tw-px-4 tw-py-3 tw-flex tw-items-center tw-justify-between tw-gap-4">
            <div>
              <div className="tw-font-semibold tw-text-gray-700">일별 추이</div>
              <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
                {formatRangeLabel(data.payload.startDate, data.payload.endDate)} 기준, 당일은 포함되지 않습니다.
              </div>
            </div>
            <div className="tw-flex tw-items-center tw-gap-2">
              <div className="tw-flex tw-gap-2 tw-mr-2">
                {[
                  { key: "daily", label: "일자별" },
                  { key: "cumulative", label: "누적" },
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setTrendViewMode(item.key as "daily" | "cumulative")}
                    className={`tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-border ${
                      trendViewMode === item.key
                        ? "tw-bg-gray-700 tw-text-white tw-border-gray-700"
                        : "tw-bg-white tw-text-gray-600 tw-border-gray-300"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              {[
                { key: "msgOpen", label: "열람 수" },
                { key: "msgClick", label: "클릭 수" },
                { key: "msgOpenRate", label: "열람률" },
                { key: "msgClickRate", label: "클릭률" },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setChartMetric(item.key as "msgOpen" | "msgClick" | "msgOpenRate" | "msgClickRate")}
                  className={`tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-border ${
                    chartMetric === item.key
                      ? "tw-bg-[#5D87FF] tw-text-white tw-border-[#5D87FF]"
                      : "tw-bg-white tw-text-gray-600 tw-border-gray-300"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <div className="tw-h-[280px] tw-p-4 tw-bg-white">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={displayedTrendData}>
                <XAxis dataKey="date" tick={{ fontSize: 12 }} padding={{ left: 16, right: 16 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => formatChartAxisValue(chartMetric, value)} />
                <Tooltip formatter={(value) => formatChartTooltipValue(chartMetric, value)} />
                <Line
                  type="monotone"
                  dataKey={chartMetric}
                  name={getChartMetricLabel(chartMetric)}
                  stroke={getChartMetricColor(chartMetric)}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="tw-border-t tw-border-gray-200 tw-bg-white">
            <div className="tw-px-4 tw-py-3 tw-font-semibold tw-text-gray-700">
              {trendViewMode === "cumulative" ? "통계 누적 날짜별" : "통계 일자별"}
            </div>
            <div className="tw-overflow-x-auto">
              <table className="tw-w-full tw-min-w-max tw-text-sm">
                <thead className="tw-bg-gray-50 tw-text-gray-500">
                  <tr>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-font-medium">일자</th>
                    <th className="tw-px-4 tw-py-3 tw-text-right tw-font-medium">발송 수</th>
                    <th className="tw-px-4 tw-py-3 tw-text-right tw-font-medium">열람 수</th>
                    <th className="tw-px-4 tw-py-3 tw-text-right tw-font-medium">열람률</th>
                    <th className="tw-px-4 tw-py-3 tw-text-right tw-font-medium">클릭 수</th>
                    <th className="tw-px-4 tw-py-3 tw-text-right tw-font-medium">클릭률</th>
                    {displayedDailyTable?.columns.map((column) => (
                      <th key={column.key} className="tw-px-4 tw-py-3 tw-text-right tw-font-medium">{column.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="tw-divide-y tw-divide-gray-100">
                  {(displayedDailyTable?.rows ?? []).map((item) => (
                    <tr key={item.date} className="tw-bg-white">
                      <td className="tw-px-4 tw-py-3 tw-text-gray-700">{item.date}</td>
                      <td className="tw-px-4 tw-py-3 tw-text-right tw-font-medium tw-text-gray-800">{item.msgSendDisplay}</td>
                      <td className="tw-px-4 tw-py-3 tw-text-right tw-font-medium tw-text-gray-800">{item.msgOpen.toLocaleString()}</td>
                      <td className="tw-px-4 tw-py-3 tw-text-right tw-font-medium tw-text-gray-800">{formatRateText(item.msgOpenRate)}</td>
                      <td className="tw-px-4 tw-py-3 tw-text-right tw-font-medium tw-text-gray-800">{item.msgClick.toLocaleString()}</td>
                      <td className="tw-px-4 tw-py-3 tw-text-right tw-font-medium tw-text-gray-800">{formatRateText(item.msgClickRate)}</td>
                      {item.detailColumns.map((column) => (
                        <td key={column.key} className="tw-px-4 tw-py-3 tw-text-right tw-font-medium tw-text-gray-800">
                          {formatMomentMetricCount(column.label, column.count)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showTrendChart && !isTrendAvailable && (
        <div className="tw-rounded-xl tw-border tw-border-amber-200 tw-bg-amber-50 tw-p-4">
          <div className="tw-font-semibold tw-text-amber-800">일별 추이</div>
          <p className="tw-mt-2 tw-text-sm tw-text-amber-700">
            이 광고의 통계는 발송 다음날({data.payload.startDate})부터 조회할 수 있습니다.
          </p>
        </div>
      )}

      <div className="tw-rounded-xl tw-border tw-border-gray-200 tw-overflow-hidden">
        <div className="tw-bg-gray-50 tw-px-4 tw-py-3 tw-font-semibold tw-text-gray-700">메시지 내용 및 미리보기</div>
        {useCompactMessageLayout ? (
          <div className="tw-p-4 tw-grid tw-grid-cols-1 xl:tw-grid-cols-[420px_minmax(0,1fr)] tw-gap-4">
            <div className="tw-rounded-lg tw-border tw-border-gray-100 tw-overflow-hidden">
              <div className="tw-bg-gray-50 tw-px-4 tw-py-3 tw-font-semibold tw-text-gray-700">미리보기</div>
              <div className="tw-p-4 tw-bg-gray-50">
                {adDetail ? <PreviewCard adDetail={adDetail} /> : null}
              </div>
            </div>
            <div className="tw-space-y-4">
              <div className="tw-rounded-lg tw-border tw-border-gray-100 tw-overflow-hidden">
                <div className="tw-bg-gray-50 tw-px-4 tw-py-3 tw-font-semibold tw-text-gray-700">광고 정보</div>
                <div className="tw-p-4">
                  {detailLoading ? (
                    <div className="tw-text-sm tw-text-gray-400">메시지 정보를 불러오는 중입니다.</div>
                  ) : adDetail ? (
                    <AdInfoCard adDetail={adDetail} />
                  ) : (
                    <div className="tw-text-sm tw-text-gray-400">메시지 정보를 불러오지 못했습니다.</div>
                  )}
                </div>
              </div>
              <div className="tw-rounded-lg tw-border tw-border-gray-100 tw-overflow-hidden">
                <div className="tw-bg-gray-50 tw-px-4 tw-py-3 tw-font-semibold tw-text-gray-700">메시지 구성</div>
                <div className="tw-p-4">
                  {detailLoading ? (
                    <div className="tw-text-sm tw-text-gray-400">메시지 정보를 불러오는 중입니다.</div>
                  ) : adDetail ? (
                    <MessageCompositionCard adDetail={adDetail} />
                  ) : (
                    <div className="tw-text-sm tw-text-gray-400">메시지 정보를 불러오지 못했습니다.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="tw-p-4 tw-grid tw-grid-cols-1 xl:tw-grid-cols-[420px_minmax(0,1fr)] tw-gap-4">
            <div className="tw-rounded-lg tw-border tw-border-gray-100 tw-overflow-hidden">
              <div className="tw-bg-gray-50 tw-px-4 tw-py-3 tw-font-semibold tw-text-gray-700">광고 정보</div>
              <div className="tw-p-4">
                {detailLoading ? (
                  <div className="tw-text-sm tw-text-gray-400">메시지 정보를 불러오는 중입니다.</div>
                ) : adDetail ? (
                  <AdInfoCard adDetail={adDetail} />
                ) : (
                  <div className="tw-text-sm tw-text-gray-400">메시지 정보를 불러오지 못했습니다.</div>
                )}
              </div>
            </div>
            <div className="tw-rounded-lg tw-border tw-border-gray-100 tw-overflow-hidden">
              <div className="tw-bg-gray-50 tw-px-4 tw-py-3 tw-font-semibold tw-text-gray-700">메시지 구성</div>
              <div className="tw-p-4">
                {detailLoading ? (
                  <div className="tw-text-sm tw-text-gray-400">메시지 정보를 불러오는 중입니다.</div>
                ) : adDetail ? (
                  <MessageCompositionCard adDetail={adDetail} />
                ) : (
                  <div className="tw-text-sm tw-text-gray-400">메시지 정보를 불러오지 못했습니다.</div>
                )}
              </div>
            </div>
            <div className="tw-rounded-lg tw-border tw-border-gray-100 tw-overflow-hidden">
              <div className="tw-bg-gray-50 tw-px-4 tw-py-3 tw-font-semibold tw-text-gray-700">미리보기</div>
              <div className="tw-p-4 tw-bg-gray-50">
                {adDetail ? <PreviewCard adDetail={adDetail} /> : null}
              </div>
            </div>
            <div className="tw-rounded-lg tw-border tw-border-gray-100 tw-overflow-hidden">
              <div className="tw-bg-gray-50 tw-px-4 tw-py-3 tw-font-semibold tw-text-gray-700">상세 통계 데이터</div>
              <div className="tw-p-4 tw-space-y-4">
                {showButton1ImageInfo && (
                  <div className="tw-rounded-lg tw-border tw-border-sky-200 tw-bg-sky-50 tw-px-4 tw-py-3 tw-text-xs tw-text-sky-800">
                    이미지가 포함된 메시지는 버튼1 클릭 수에 이미지 클릭이 함께 반영될 수 있습니다.
                  </div>
                )}
                {clickSummaryItems.length > 0 ? (
                  <DetailedClickSections messageType={adDetail?.messageType as MomentMessageType | undefined} items={clickSummaryItems} />
                ) : (
                  <div className="tw-text-sm tw-text-gray-400">표시할 상세 통계가 없습니다.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function extractFirstRow(data: object | null): ReportRow | null {
  if (!data || typeof data !== "object") return null;
  const obj = data as Record<string, unknown>;

  if (Array.isArray(obj.rows) && obj.rows.length > 0) {
    return obj.rows[0] as ReportRow;
  }
  if (Array.isArray(obj.data) && obj.data.length > 0) {
    return obj.data[0] as ReportRow;
  }
  return null;
}

function isEmptyLiveReportData(data: DisplayReportData | null) {
  if (!data || data.mode !== "live") {
    return false;
  }
  const payload = data.payload as Record<string, unknown>;
  if (Array.isArray(payload.rows)) {
    return payload.rows.length === 0;
  }
  if (Array.isArray(payload.data)) {
    return payload.data.length === 0;
  }
  return false;
}

function formatMetricValue(key: string, value: unknown) {
  if (typeof value !== "number") {
    return String(value ?? "-");
  }
  if (key.includes("rate")) {
    return formatRateText(value);
  }
  if (key === "cost") {
    return `${value.toLocaleString()}P`;
  }
  if (key.startsWith("cost_per_")) {
    return `${formatCostPerText(value)}P`;
  }
  return value.toLocaleString();
}

function formatCostPerText(value: number) {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatRateText(value: string | number | null | undefined) {
  const numericValue = Number(value ?? 0);
  if (!Number.isFinite(numericValue)) {
    return "0%";
  }
  return `${Math.round(numericValue)}%`;
}

function fillTrendDates(startDate: string, endDate: string, trend: StoredReportDto["trend"]) {
  const map = new Map(trend.map((item) => [item.date, item]));
  const result: StoredReportDto["trend"] = [];
  const current = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  while (current <= end) {
    const date = formatLocalDate(current);
    result.push(map.get(date) ?? { date, msgSend: 0, msgOpen: 0, msgClick: 0, msgOpenRate: "0", msgClickRate: "0" });
    current.setDate(current.getDate() + 1);
  }
  return result;
}

function formatLocalDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatRangeLabel(startDate: string, endDate: string) {
  return `${startDate} ~ ${endDate}`;
}

function parseSendDate(sendDate?: string | null) {
  if (!sendDate || sendDate.length < 10) return null;
  const parsed = new Date(`${sendDate.substring(0, 10)}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function resolveDefaultReportPreset(
  sendDate: string | null | undefined,
  availableOptions: Array<{ value: string; label: string }>
) {
  const send = parseSendDate(sendDate);
  const values = new Set(availableOptions.map((option) => option.value));
  if (!send) {
    return availableOptions[0]?.value ?? "TODAY";
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((today.getTime() - send.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays <= 0 && values.has("TODAY")) {
    return "TODAY";
  }
  if (diffDays === 1 && values.has("YESTERDAY")) {
    return "YESTERDAY";
  }
  if (values.has("LAST_7DAY")) {
    return "LAST_7DAY";
  }
  return availableOptions[0]?.value ?? "TODAY";
}

function buildDailyStatTableRows(
  messageType: MomentMessageType,
  messageBody: Record<string, unknown>,
  sendDate: string | null,
  trend: StoredReportDto["trend"],
  dailyClickStats: StoredReportDto["dailyClickStats"]
) {
  const sendDateOnly = sendDate?.substring(0, 10) ?? null;
  const clickMapByDate = dailyClickStats.reduce<Record<string, Record<string, unknown>>>((acc, item) => {
    if (!acc[item.date]) {
      acc[item.date] = {};
    }
    acc[item.date][item.key] = item.count;
    return acc;
  }, {});

  const rows = trend.map((item) => {
    const metricMap = {
      ...clickMapByDate[item.date],
      msg_open_rate: Number(item.msgOpenRate ?? 0),
      msg_click_rate: Number(item.msgClickRate ?? 0),
    };
    const detailColumns = buildMomentStructuredClickSummary(messageType, messageBody, metricMap, false);
    return {
      ...item,
      msgSendDisplay: sendDateOnly && item.date !== sendDateOnly ? "-" : item.msgSend.toLocaleString(),
      detailColumns,
    };
  });

  const columnMap = new Map<string, string>();
  rows.forEach((row) => {
    row.detailColumns.forEach((column) => {
      if (!columnMap.has(column.key)) {
        columnMap.set(column.key, column.label);
      }
    });
  });

  const columns = Array.from(columnMap.entries()).map(([key, label]) => ({ key, label }));
  const normalizedRows = rows.map((row) => {
    const detailMap = new Map(row.detailColumns.map((column) => [column.key, column]));
    return {
      ...row,
      detailColumns: columns.map((column) => ({
        key: column.key,
        label: column.label,
        count: detailMap.get(column.key)?.count ?? 0,
      })),
    };
  });

  return { columns, rows: normalizedRows };
}

function buildCumulativeDailyTable(
  table: ReturnType<typeof buildDailyStatTableRows> | null
) {
  if (!table) return null;
  const runningDetail = new Map<string, number>();
  const baseMsgSend = table.rows.find((row) => row.msgSend > 0)?.msgSend ?? table.rows[0]?.msgSend ?? 0;
  let msgOpen = 0;
  let msgClick = 0;
  return {
    columns: table.columns,
    rows: table.rows.map((row) => {
      msgOpen += row.msgOpen;
      msgClick += row.msgClick;
      const detailColumns = row.detailColumns.map((column) => {
        const nextValue = (runningDetail.get(column.key) ?? 0) + column.count;
        runningDetail.set(column.key, nextValue);
        return { ...column, count: nextValue };
      });
      return {
        ...row,
        msgSend: baseMsgSend,
        msgOpen,
        msgClick,
        msgSendDisplay: baseMsgSend.toLocaleString(),
        msgOpenRate: baseMsgSend === 0 ? "0" : formatRateNumber((msgOpen / baseMsgSend) * 100),
        msgClickRate: baseMsgSend === 0 ? "0" : formatRateNumber((msgClick / baseMsgSend) * 100),
        detailColumns,
      };
    }),
  };
}

function buildCumulativeTrendData(trend: StoredReportDto["trend"]) {
  const baseMsgSend = trend.find((item) => item.msgSend > 0)?.msgSend ?? trend[0]?.msgSend ?? 0;
  let msgOpen = 0;
  let msgClick = 0;
  return trend.map((item) => {
    msgOpen += item.msgOpen;
    msgClick += item.msgClick;
    return {
      ...item,
      msgSend: baseMsgSend,
      msgOpen,
      msgClick,
      msgOpenRate: baseMsgSend === 0 ? "0" : formatRateNumber((msgOpen / baseMsgSend) * 100),
      msgClickRate: baseMsgSend === 0 ? "0" : formatRateNumber((msgClick / baseMsgSend) * 100),
    };
  });
}

function formatRateNumber(value: number) {
  return (Math.round(value * 100) / 100).toString();
}

function formatChartAxisValue(metric: "msgOpen" | "msgClick" | "msgOpenRate" | "msgClickRate", value: number | string) {
  const numericValue = Number(value ?? 0);
  if (metric === "msgOpenRate" || metric === "msgClickRate") {
    return formatRateText(numericValue);
  }
  return Math.round(numericValue).toLocaleString();
}

function formatChartTooltipValue(
  metric: "msgOpen" | "msgClick" | "msgOpenRate" | "msgClickRate",
  value: number | string | readonly (string | number)[] | undefined
) {
  const rawValue = Array.isArray(value) ? value[0] : value;
  const numericValue = Number(rawValue ?? 0);
  if (metric === "msgOpenRate" || metric === "msgClickRate") {
    return [formatRateText(numericValue), getChartMetricLabel(metric)];
  }
  return [Math.round(numericValue).toLocaleString(), getChartMetricLabel(metric)];
}

function getChartMetricLabel(metric: "msgOpen" | "msgClick" | "msgOpenRate" | "msgClickRate") {
  if (metric === "msgOpen") return "열람 수";
  if (metric === "msgClick") return "클릭 수";
  if (metric === "msgOpenRate") return "열람률";
  return "클릭률";
}

function getChartMetricColor(metric: "msgOpen" | "msgClick" | "msgOpenRate" | "msgClickRate") {
  if (metric === "msgOpen") return "#34C759";
  if (metric === "msgClick") return "#FF6B6B";
  if (metric === "msgOpenRate") return "#5D87FF";
  return "#A855F7";
}

function isValidStoredRange(startDate: string, endDate: string) {
  if (!startDate || !endDate) return false;
  return startDate <= endDate;
}

function AdInfoCard({ adDetail }: { adDetail: MomentAdDetailDto }) {
  const body = adDetail.messageBody ?? {};
  const typeLabel = MOMENT_MESSAGE_TYPE_OPTIONS.find((option) => option.value === adDetail.messageType)?.label ?? adDetail.messageType;

  return (
    <div className="tw-space-y-2 tw-text-sm">
      <div><span className="tw-text-gray-500">광고명</span><span className="tw-ml-2 tw-font-medium tw-text-gray-800">{adDetail.adName}</span></div>
      <div><span className="tw-text-gray-500">메시지 유형</span><span className="tw-ml-2 tw-font-medium tw-text-gray-800">{typeLabel}</span></div>
      <div><span className="tw-text-gray-500">발송일시</span><span className="tw-ml-2 tw-font-medium tw-text-gray-800">{adDetail.sendDate ? adDetail.sendDate.replace("T", " ").substring(0, 16) : "-"}</span></div>
      {"adFlag" in body ? <div><span className="tw-text-gray-500">광고 표기</span><span className="tw-ml-2">{body.adFlag ? "사용" : "미사용"}</span></div> : null}
    </div>
  );
}

function MessageCompositionCard({ adDetail }: { adDetail: MomentAdDetailDto }) {
  const body = adDetail.messageBody ?? {};
  const buttons = Array.isArray(body.buttons) ? body.buttons : [];
  const listItems = Array.isArray(body.items) ? body.items : [];
  const carousels = Array.isArray(body.carousels) ? body.carousels : [];
  const hasMainImage = Boolean(body.imageUrl) || listItems.length > 0;
  const hasCommerceIntro = adDetail.messageType === "CAROUSEL_COMMERCE_MESSAGE" && Boolean(body.introCarousel);
  const hasShare = resolveMessageHasShare(adDetail.messageType as MomentMessageType, body);

  return (
    <div className="tw-space-y-2 tw-text-sm tw-text-gray-700">
      {"messageTitle" in body && body.messageTitle ? <div><span className="tw-text-gray-500">타이틀</span><span className="tw-ml-2">{String(body.messageTitle)}</span></div> : null}
      {"description" in body && body.description ? <div><span className="tw-text-gray-500">홍보 문구</span><span className="tw-ml-2">{String(body.description)}</span></div> : null}
      <div><span className="tw-text-gray-500">공유하기</span><span className="tw-ml-2">{hasShare ? "사용" : "미사용"}</span></div>
      {hasMainImage && (adDetail.messageType === "WIDE_MESSAGE" || adDetail.messageType === "WIDE_LIST_MESSAGE") ? (
        <div><span className="tw-text-gray-500">이미지</span><span className="tw-ml-2">사용</span></div>
      ) : null}
      {buttons.length > 0 ? <div><span className="tw-text-gray-500">메시지 버튼 수</span><span className="tw-ml-2">{buttons.length}개</span></div> : null}
      {listItems.length > 0 && adDetail.messageType === "WIDE_LIST_MESSAGE" ? <div><span className="tw-text-gray-500">리스트 수</span><span className="tw-ml-2">{listItems.length}개</span></div> : null}
      {carousels.length > 0 && (adDetail.messageType === "CAROUSEL_FEED_MESSAGE" || adDetail.messageType === "CAROUSEL_COMMERCE_MESSAGE") ? (
        <div><span className="tw-text-gray-500">캐러셀 카드 수</span><span className="tw-ml-2">{carousels.length}개</span></div>
      ) : null}
      {hasCommerceIntro ? <div><span className="tw-text-gray-500">인트로</span><span className="tw-ml-2">사용</span></div> : null}
      {"couponBook" in body && body.couponBook ? <div><span className="tw-text-gray-500">쿠폰</span><span className="tw-ml-2">사용</span></div> : null}
      {"moreButton" in body && body.moreButton ? <div><span className="tw-text-gray-500">더보기</span><span className="tw-ml-2">사용</span></div> : null}
    </div>
  );
}

function PreviewCard({ adDetail }: { adDetail: MomentAdDetailDto }) {
  const previewProps = buildPreviewProps(adDetail);
  if (!previewProps) return null;
  return <MomentPhonePreview {...previewProps} />;
}

function buildPreviewProps(adDetail: MomentAdDetailDto) {
  const messageBody = adDetail.messageBody ?? {};
  const messageType = adDetail.messageType as MomentMessageType;
  const typeOption = MOMENT_MESSAGE_TYPE_OPTIONS.find((option) => option.value === messageType);
  if (!typeOption) return null;

  const buttons = ensureArray(messageBody.buttons).map((button, index) => ({
    id: String(button?.id ?? `btn-${index + 1}`),
    title: String(button?.title ?? ""),
    pcLandingUrl: String(button?.pcLandingUrl ?? ""),
    mobileLandingUrl: String(button?.mobileLandingUrl ?? ""),
  })) as MomentButton[];

  const coupon = normalizeCoupon(messageBody.couponBook);
  const listItems = ensureArray(messageBody.items).map((item, index) => ({
    id: String(item?.id ?? `item-${index + 1}`),
    description: String(item?.description ?? ""),
    imagePreview: String(item?.imageUrl ?? "") || null,
    imageName: null,
    imageFile: null,
    imageUploadedUrl: String(item?.imageUrl ?? ""),
    pcLandingUrl: String(item?.pcLandingUrl ?? ""),
    mobileLandingUrl: String(item?.mobileLandingUrl ?? ""),
  })) as MomentListItem[];
  const rawItems = ensureArray(messageBody.items);

  const carouselItems = ensureArray(messageBody.carousels).map((item, index) => ({
    id: String(item?.id ?? `carousel-${index + 1}`),
    messageTitle: String(item?.messageTitle ?? ""),
    description: String(item?.description ?? ""),
    imagePreview: String(item?.imageUrl ?? "") || null,
    imageName: null,
    imageFile: null,
    imageUploadedUrl: String(item?.imageUrl ?? ""),
    pcLandingUrl: String(item?.landing?.pcLandingUrl ?? item?.pcLandingUrl ?? ""),
    mobileLandingUrl: String(item?.landing?.mobileLandingUrl ?? item?.mobileLandingUrl ?? ""),
    buttonTitle: String(item?.buttons?.[0]?.title ?? item?.buttonTitle ?? ""),
    buttonPcLandingUrl: String(item?.buttons?.[0]?.link?.pcLandingUrl ?? item?.buttonPcLandingUrl ?? ""),
    buttonMobileLandingUrl: String(item?.buttons?.[0]?.link?.mobileLandingUrl ?? item?.buttonMobileLandingUrl ?? ""),
    secondaryButtonTitle: String(item?.buttons?.[1]?.title ?? item?.secondaryButtonTitle ?? ""),
    secondaryButtonPcLandingUrl: String(item?.buttons?.[1]?.link?.pcLandingUrl ?? item?.secondaryButtonPcLandingUrl ?? ""),
    secondaryButtonMobileLandingUrl: String(item?.buttons?.[1]?.link?.mobileLandingUrl ?? item?.secondaryButtonMobileLandingUrl ?? ""),
    secondaryButtonEnabled: Boolean(item?.secondaryButtonEnabled ?? item?.buttons?.[1]),
    priceAmount: String(item?.priceAmount ?? ""),
    discountedPriceAmount: String(item?.discountedPriceAmount ?? ""),
    shareFlag: Boolean(item?.shareFlag),
    couponEnabled: Boolean(item?.couponBook),
    coupon: normalizeCoupon(item?.couponBook),
  })) as MomentCarouselItem[];

  const commerceIntro = normalizeIntro(messageBody.introCarousel);
  const firstImage =
    messageType === "WIDE_MESSAGE"
      ? String((rawItems[0] as Record<string, unknown> | undefined)?.imageUrl ?? "")
      : String(messageBody.imageUrl ?? "");

  return {
    channelLabel: adDetail.channelName || "비즈톡",
    messageName: adDetail.adName,
    body: String(messageBody.description ?? ""),
    messageTitle: String(messageBody.messageTitle ?? ""),
    imagePreview: firstImage || null,
    typeOption,
    messageType,
    buttons,
    buttonsEnabled: buttons.length > 0,
    ageAuthEnabled: false,
    adFlag: Boolean(messageBody.adFlag),
    shareFlag: Boolean(messageBody.shareFlag),
    couponEnabled: Boolean(messageBody.couponBook),
    coupon,
    moreButtonEnabled: Boolean(messageBody.moreButton),
    listItems,
    carouselItems,
    commerceIntroEnabled: Boolean(messageBody.introCarousel),
    commerceIntro,
  };
}

function DetailedClickSections({
  messageType,
  items,
}: {
  messageType?: MomentMessageType;
  items: Array<{ key: string; label: string; count: number }>;
}) {
  const sections = buildDetailedClickSections(messageType, items);
  return (
    <div className="tw-space-y-4">
      {sections.map((section) => (
        <div key={section.title} className="tw-rounded-lg tw-border tw-border-gray-100 tw-overflow-hidden">
          <div className="tw-bg-gray-50 tw-px-4 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-gray-700">{section.title}</div>
          <div className="tw-divide-y tw-divide-gray-100">
            {section.items.map((item) => (
              <div key={item.key} className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-text-sm">
                <span className="tw-text-gray-500">{stripSectionPrefix(item.label, section.prefix)}</span>
                <span className="tw-font-semibold tw-text-gray-800">{formatMomentMetricCount(item.label, item.count)}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function buildDetailedClickSections(
  messageType: MomentMessageType | undefined,
  items: Array<{ key: string; label: string; count: number }>
) {
  if (messageType === "CAROUSEL_FEED_MESSAGE" || messageType === "CAROUSEL_COMMERCE_MESSAGE") {
    const sectionMap = new Map<string, Array<{ key: string; label: string; count: number }>>();
    const commonItems: Array<{ key: string; label: string; count: number }> = [];
    const introItems: Array<{ key: string; label: string; count: number }> = [];
    items.forEach((item) => {
      if (messageType === "CAROUSEL_COMMERCE_MESSAGE" && item.key === "msg_click_media") {
        introItems.push(item);
        return;
      }
      const match = item.label.match(/^(카드 \d+)\s+/);
      if (!match) {
        commonItems.push(item);
        return;
      }
      const title = match[1];
      if (!sectionMap.has(title)) {
        sectionMap.set(title, []);
      }
      sectionMap.get(title)!.push(item);
    });
    const sections: Array<{ title: string; prefix: string; items: Array<{ key: string; label: string; count: number }> }> = [];
    if (introItems.length > 0) {
      sections.push({ title: "인트로 카드", prefix: "인트로 ", items: introItems });
    }
    sections.push(...Array.from(sectionMap.entries()).map(([title, sectionItems]) => ({
      title,
      prefix: `${title} `,
      items: sectionItems,
    })));
    if (commonItems.length > 0) {
      sections.push({ title: "공통", prefix: "", items: commonItems });
    }
    return sections;
  }

  if (messageType === "WIDE_LIST_MESSAGE") {
    const listItems = items.filter((item) => /^리스트 \d+/.test(item.label));
    const commonItems = items.filter((item) => !/^리스트 \d+/.test(item.label));
    const sections: Array<{ title: string; prefix: string; items: Array<{ key: string; label: string; count: number }> }> = [];
    if (listItems.length > 0) {
      sections.push({ title: "리스트", prefix: "", items: listItems });
    }
    if (commonItems.length > 0) {
      sections.push({ title: "공통", prefix: "", items: commonItems });
    }
    return sections;
  }

  return [{ title: "공통", prefix: "", items }];
}

function stripSectionPrefix(label: string, prefix: string) {
  return prefix && label.startsWith(prefix) ? label.slice(prefix.length) : label;
}

function resolveMessageHasShare(messageType: MomentMessageType, body: Record<string, unknown>) {
  if (Boolean(body.shareFlag)) {
    return true;
  }
  if (messageType === "CAROUSEL_FEED_MESSAGE" || messageType === "CAROUSEL_COMMERCE_MESSAGE") {
    return (Array.isArray(body.carousels) ? body.carousels : []).some((item) => {
      if (!item || typeof item !== "object") {
        return false;
      }
      return Boolean((item as Record<string, unknown>).shareFlag);
    });
  }
  return false;
}

function hasImageBoundButton1(messageType: MomentMessageType) {
  return messageType === "WIDE_MESSAGE" || messageType === "CAROUSEL_FEED_MESSAGE" || messageType === "CAROUSEL_COMMERCE_MESSAGE";
}

function normalizeCoupon(value: unknown): MomentCoupon {
  if (!value || typeof value !== "object") return createMomentCoupon();
  const coupon = value as Record<string, unknown>;
  return {
    couponBookTitleType: (coupon.couponBookTitleType as MomentCoupon["couponBookTitleType"]) ?? "UPGRADE",
    couponBookTitle: String(coupon.couponBookTitle ?? ""),
    title: String(coupon.title ?? ""),
    pcLandingUrl: String(coupon.pcLandingUrl ?? ""),
    mobileLandingUrl: String(coupon.mobileLandingUrl ?? ""),
  };
}

function normalizeIntro(value: unknown): MomentCommerceIntro {
  const base = createMomentCommerceIntro();
  if (!value || typeof value !== "object") return base;
  const intro = value as Record<string, unknown>;
  return {
    ...base,
    messageTitle: String(intro.messageTitle ?? ""),
    description: String(intro.description ?? ""),
    imagePreview: String(intro.imageUrl ?? "") || null,
    imageUploadedUrl: String(intro.imageUrl ?? ""),
    pcLandingUrl: String((intro.landing as Record<string, unknown> | undefined)?.pcLandingUrl ?? ""),
    mobileLandingUrl: String((intro.landing as Record<string, unknown> | undefined)?.mobileLandingUrl ?? ""),
  };
}
